/* global ApplePaySession */
import { formatPrice } from '@ynap/intl-formatters'
import { getLineItem, getCardTypes, getPrice, getShippingOptions } from '@ynap/cart-state'

import { priceFromPriceObj } from '@ynap/price-utils'
import { getAnalyticsConfig } from '@ynap/config-state'
import { createCombinedSelector } from '../utils/selectorUtils'

import getTotals from './getTotals'
import getAnalytics from './getAnalytics'
import isPaymentRequired from './isPaymentRequired'
import { sortJITItemsToBottom, splitRemoteItems } from '../../../utils/jit'
import { isPaymentEnabled } from '../../../utils/payment-utils'
import { rectifyItemsByOrderMessage } from '../utils/rectifyItemsByOrderMessage'
import { Messages, OrderItem, OrderItemIncludingRemoved, ShoppingCart, UsablePaymentInformation } from 'app/types/common-types'
import { Config, LineItem, Locale, StoreState, Totals } from 'app/state/types'



export default createCombinedSelector({
    name: 'getCart',
    state: ['cart', 'locale', 'config', 'deviceType'],
    props: ['messages'],
    passThroughState: [
        'isCheckingOut',
        'checkoutError',
        'removeLineItemError',
        'removeLineItemsError',
        'moveToWishlistError',
        'undoMoveToWishlistError',
        'checkoutNotification',
        'isFetchingCart',
        'fetchCartError',
        'addItemsToWishlistError',
        'updateLineItemInBasketError',
        'promotionsCheckerInfo',
        'activePromotions',
        'isFetchingProvinces',
        'klarnaExpress',
    ],
    fn: getCartInternal,
})

interface State extends StoreState {
    cart: ShoppingCart
}

type Props = {
    messages: Messages
}

type ApplePayTotal = {
    label: string;
    amount: string | number;
    type: string;
};

type Details = OrderItem | OrderItemIncludingRemoved | undefined

export function getCartInternal(state: State, props: Props) {
    const { messages } = props
    const { locale = {}, cart = {} as ShoppingCart, config = { head: {} } as Config, activePromotions, promotionsCheckerInfo, isFetchingProvinces } = state

    const {
        adjustment,
        orderItem = [],
        orderItemIncludingRemoved,
        grandTotal,
        x_deliveryDuty,
        x_dduAcceptance,
        usablePaymentInformation = [],
        removedItems = [],
        rectification,
        orderId,
        x_buyerId: buyerId,
        genericTotal,
        x_selfEmailAddress,
        x_totalOrderQuantity,
        x_userStatus: userStatus,
        x_isCSR,
        x_taxType,
        x_newUser,
        x_totalSavings,
        x_exchangeRate,
        x_baseOrderTotal_Annualised_GBP,
        x_baseOrderTotal,
        x_sumLineTotal,
        x_isGift: isGift,
        totalTaxByTaxCategory,
        paymentInstruction,
        totalSalesTax,
        promotionCode,
        'x_attr-donationAvailable': donationAvailable,
        'x_attr-donationCharities': donationCharities,
        'x_attr-availableStoreCreditAmount': availableStoreCreditAmount,
        x_shipmentType: shipmentType = 'PRE',
        donationOrderId,
        orderMessages = [],
        x_omniStockMultiShipmentOptions,
        rectifiedItemsList,
        x_shipments,
    } = cart

    const availableStoreCredit = availableStoreCreditAmount ? availableStoreCreditAmount.amount / availableStoreCreditAmount.divisor : 0
    const removedItemsRestrictions = orderMessages.map(({ code }) => code) || []
    const hasShippingMethodChanged = !!orderMessages.find(({ code }) => code === 'SHIPPING_METHOD_INVALID')
    const [shippingOptions] = getShippingOptions(state, props)
    const { xumet_merchantId: klarnaMerchantId, xumet_clientId: klarnaClientId } =
        usablePaymentInformation.find((method) => method.paymentMethodName === 'KLARNA') || {}
    const firstTimeUser = x_newUser === 'true' || userStatus === 'G'
    const isCSR = x_isCSR === 'true'

    // get reference list of local and remote products
    const [hasMultiShipments, itemShipmentLookup] = getShipments(x_shipments)
    const isPostSplit = x_omniStockMultiShipmentOptions === 'true' && shipmentType === 'POST' && hasMultiShipments

    let hasJITItems = false
    const lineItemsIncludingRemoved = orderItemIncludingRemoved
        ? orderItemIncludingRemoved.map((item) => {
              const details: Details = item.removed
                  ? item
                  : orderItem.find(({ orderItemId, partNumber }) => orderItemId === item.orderItemId || (!item.orderItemId && partNumber === item.partNumber))
            
            let quantityInStock = 0
            if (details && 'InventoryAvailability' in details) {
                quantityInStock = details?.InventoryAvailability?.[0]?.onlineInventory || 0
            }
            const lineLevelTaxesEnabled = config?.head?.flags?.lineLevelTaxesEnabled
            const price =
                    details && details.catalogEntryView && Object.keys(details.catalogEntryView).length > 0
                    ? getPrice(details, locale.id, x_taxType, lineLevelTaxesEnabled)
                    : null
           
            details.isRemote = itemShipmentLookup?.[details.orderItemId] === true
            if (details && 'xitem_local_stock' in details && details?.xitem_local_stock === 'false') {
                hasJITItems = true
            }

            return getLineItem(state, details, { inOrder: details?.quantity, inStock: quantityInStock }, messages, price || {}, locale)
          })
        : undefined

    const rectifiedQtyItems = rectifyItemsByOrderMessage(orderMessages)
    const lineItems: LineItem[] | undefined  = lineItemsIncludingRemoved
        ? lineItemsIncludingRemoved.filter((item) => (item && !item.removed) || rectifiedItemsList?.includes(Number(item.partNumber)))
        : undefined

    const numItems = x_totalOrderQuantity

    const soldoutItems = getSoldoutItemsByOrderMessage(rectifiedQtyItems, removedItems, state, messages)

    const restrictedItems = (lineItems || []).filter((lineItem) => lineItem.isHazardous)

    const DDPCountry = x_taxType === 'DDP'
    const DDPIncCountry = x_taxType === 'DDP_INCLUDED'

    const applePayConfig = config?.head?.applepay

    const canMakeApplePayments =
        typeof window !== 'undefined' &&
        window?.ApplePaySession &&
        ApplePaySession.canMakePayments() &&
        ApplePaySession.supportsVersion(applePayConfig?.version || 8)
    const applePayEnabled = isPaymentEnabled(applePayConfig, locale?.country, canMakeApplePayments || false)

    const klarnaEnabled = config?.head?.klarna?.enabled ?? false
    const affirmEnabled = config?.head?.affirm?.enabled ?? false

    let cardTypes = []

    // generate list of payment methods for this cart.
    const paymentMethods = usablePaymentInformation.reduce<string[]>((methods, item) => {
        // get list of available cards for this purchase
        if (item.paymentMethodName === 'CREDIT_CARD') {
            cardTypes = getCardTypes(item.xumet_card_types)
            return [...methods, ...cardTypes]
        }
        // add in paypal/store credit if available
        if ((item.paymentMethodName.match(/^(PAYPAL)$/) && !item.xumet_billingAgreement) || item.paymentMethodName.match(/^(STORE_CREDIT)$/)) {
            return [...methods, item.paymentMethodName]
        }
        // add in only one klarna method if available (this is only used for Basket payment methods)
        if (klarnaEnabled && item.paymentMethodName.match(/^KLARNA\w*$/) && !methods.includes('KLARNA_PAY_NOW')) {
            return [...methods, 'KLARNA_PAY_NOW']
        }

        // add in applepay if available and enabled
        if (applePayEnabled && item.paymentMethodName.match(/^(APPLEPAY)$/)) {
            return [...methods, item.paymentMethodName]
        }

        // add affirm payment method
        if (affirmEnabled && item.paymentMethodName === 'AFFIRM') {
            return [...methods, item.paymentMethodName]
        }
        return methods
    }, [])

    const saleItemsInOrder = x_totalSavings?.amount > 0
    const totals = grandTotal ? getTotals(state, props) : undefined
    const basePriceGBP = x_baseOrderTotal_Annualised_GBP || x_baseOrderTotal || totals?.priceWithPromotion
    const basePrice = x_baseOrderTotal || totals?.priceWithPromotion
    const itemSubtotal = x_sumLineTotal?.amount / x_sumLineTotal?.divisor
    const transactionTotal = grandTotal?.amount / grandTotal?.divisor
    const analytics = getAnalyticsConfig(state)
    const useIdentifier: boolean = config?.head?.flags?.useIdentifier

    // used for sidebar bag
    const [remoteItems, localItems] = splitRemoteItems(lineItems)

    // used for main shopping bag - we want removed items so that we can display them here
    const [remoteItemsIncRemoved, localItemsIncRemoved] = splitRemoteItems(lineItemsIncludingRemoved || [])

    lineItems && sortJITItemsToBottom(lineItems)

    const analyticsProps = {
        x_taxType,
        totalTaxByTaxCategory,
        paymentInstruction,
        totalSalesTax,
        lineItems,
        totals,
        orderId,
        genericTotal,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        analytics,
        locale,
        x_exchangeRate,
        userStatus,
        useIdentifier,
    }

    const applePaySupportedNetworks = getSupportedNetworks(
        usablePaymentInformation.find(({ paymentMethodName }) => paymentMethodName === 'APPLEPAY')?.xumet_supportedCardTypes
    )
    const getApplePayRequest = () => {
        const applePayTotals = getApplePayTotals(totals, messages)

        return {
            countryCode: locale?.country?.toUpperCase(),
            currencyCode: totals?.currency,
            supportedNetworks: applePaySupportedNetworks || applePayConfig?.supportedNetworks,
            merchantCapabilities: applePayConfig?.merchantCapabilities,
            total: { label: messages?.paymentOptionsForm?.applePayBrandLabel(), amount: totals?.rawTotal },
            ...(applePayTotals.length > 0 ? { lineItems: applePayTotals } : []),
        }
    }

    return {
        adjustment,
        hasJITItems,
        hasMultiShipments,
        locale,
        config: config.head,
        lineItems,
        lineItemsIncludingRemoved,
        remoteItems,
        localItems,
        remoteItemsIncRemoved,
        localItemsIncRemoved,
        rectifiedQtyItems,
        rectifiedItemsList,
        soldoutItems,
        restrictedItems,
        numItems,
        paymentMethods,
        paymentRequired: isPaymentRequired(state),
        orderId,
        buyerId,
        totals,
        analytics,
        rectification,
        genericTotal,
        x_selfEmailAddress,
        userStatus,
        isCSR,
        orderItem,
        DDPCountry,
        DDPIncCountry,
        deliveryDuty: x_deliveryDuty,
        dduAcceptance: x_dduAcceptance,
        firstTimeUser,
        saleItemsInOrder,
        x_exchangeRate,
        basePriceGBP,
        basePrice,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics: getAnalytics(analyticsProps),
        promotionCode: promotionCode?.[0]?.code,
        donationAvailable,
        donationCharities,
        donationOrderId,
        getApplePayRequest,
        isGift,
        removedItemsRestrictions,
        shipmentType,
        hasShippingMethodChanged,
        activePromotions,
        promotionsCheckerInfo,
        isPostSplit,
        shippingOptions,
        isFetchingProvinces,
        klarnaMerchantId,
        klarnaClientId,
        getApplePayTotals,
        availableStoreCredit,
    }
}

export function getSoldoutItemsByOrderMessage(rectifiedQtyItems, removedItems: LineItem[], state: State, messages: Messages) {
    if (rectifiedQtyItems?.length) {
        return removedItems
            .filter((item) => item?.partNumber === rectifiedQtyItems?.parameters)
            .map((item) => {
                const { partNumber, removed, giftListItemID, alreadyInWishlist } = item

                const details = {
                    catalogEntryView: item.catalogEntryView,
                    partNumber,
                    removed,
                    giftListItemID,
                    alreadyInWishlist,
                }

                return getLineItem(state, details, { inOrder: 0, inStock: 0 }, messages)
            })
    } else {
        return removedItems.map((item) => {
            const { partNumber, removed, giftListItemID, alreadyInWishlist } = item

            const details = {
                catalogEntryView: item.catalogEntryView,
                partNumber,
                removed,
                giftListItemID,
                alreadyInWishlist,
            }

            return getLineItem(state, details, { inOrder: 0, inStock: 0 }, messages)
        })
    }
}

export function getApplePayTotals(totals: Totals, messages: Messages) {
    let applePayTotals: ApplePayTotal[] = []
    const { rawPriceWithTax, rawShippingPrice, rawStoreCredit, rawOrderTaxes } = totals

    const priceBreakdown = {
        total: { label: messages?.totals?.itemTotal?.(), amount: rawPriceWithTax },
        shippingPrice: { label: messages?.totals?.shipping?.({}), amount: rawShippingPrice },
        storeCredit: { label: messages?.totals?.storeCreditLabel?.(), amount: rawStoreCredit && `-${rawStoreCredit}` },
        customsFees: { label: messages?.totals?.brokerage?.(), amount: rawOrderTaxes?.BROKERAGE_FEE },
    }

    Object.values(priceBreakdown).forEach(({ label, amount }) => {
        if (amount && label) {
            applePayTotals.push({ label, amount, type: 'final' })
        }
    })

    applePayTotals = applePayTotals.length > 1 ? applePayTotals : []
    return applePayTotals
}

export function getUserStatus(state: State) {
    const { cart = {} as ShoppingCart } = state
    const { x_userStatus: userStatus } = cart
    return userStatus
}

export function getPaymentSystem(state: State) {
    const { cart: { x_paymentSystem = 'GPS' } = {} } = state
    return x_paymentSystem
}

export function getOrderTaxes(taxes, locale: Locale) {
    if (typeof taxes === 'undefined' || taxes === null) {
        return {}
    }

    const output = {}
    taxes.forEach(({ taxCategoryCode, value, currency }) => {
        if (value?.amount > 0) {
            output[taxCategoryCode] = formatPrice(priceFromPriceObj(value), currency.label, locale)
        }
    })
    return output
}

export function getSupportedNetworks(cardList) {
    if (!Array.isArray(cardList)) return null

    const map = {
        VISA: 'visa',
        VISA_ELECTRON: 'electron',
        MASTERCARD: 'masterCard',
        AMERICAN_EXPRESS: 'amex',
        DISCOVER: 'discover',
        JCB: 'jcb',
        MAESTRO: 'maestro',
    }

    return cardList.map((card) => map[card]).filter((card) => card)
}

export function rectifyQuantity(orderMessages) {
    const rectifiedQuantityItems = orderMessages
        .filter((item) => item.code === 'ITEM_STOCK_REDUCED' || item.code === 'CART_LIMIT_QTY_REDUCED')
        .map((item) => ({
            partNumber: Number(item?.parameters[1]),
            reducedFrom: item?.parameters[3],
            reducedTo: item?.parameters[2],
        }))

    return rectifiedQuantityItems
}

// returns flag to show if we have both local and remote shipments
// plus a lookup table of order item IDs with a true value if remote or false if local
export function getShipments(x_shipments) {
    if (!x_shipments) {
        return [false, {}]
    }

    const lookup = {}
    let hasLocal = false
    let hasRemote = false

    x_shipments.forEach(({ shipmentType, shipmentItems }) => {
        shipmentItems.forEach((currentItem) => {
            lookup[currentItem.orderItemId] = shipmentType === 'REMOTE'
        })

        if (shipmentType === 'LOCAL') {
            hasLocal = true
        } else {
            hasRemote = true
        }
    })

    return [hasLocal && hasRemote, lookup]
}
